import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { PageBreadcrumb } from '@/components/seo/PageBreadcrumb';
import { getPublicSiteUrl, stripHtmlForMeta } from '@/lib/seo';
import { breadcrumbListJsonLd, webPageJsonLd, jsonLdScript } from '@/lib/schema-org';
import {
  HREFLANG_LOCALE_CODES,
  buildPathForLocale,
  resolveStaticPageSlug,
} from '@/lib/intl-seo';

const LANG_CODES = new Set(HREFLANG_LOCALE_CODES);

const DATE_LOCALE_MAP = {
  tr: 'tr-TR',
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  pt: 'pt-PT',
  ru: 'ru-RU',
  ar: 'ar-SA',
  zh: 'zh-CN',
  fa: 'fa-IR',
};

function getI18nStaticPageContent(t, slug) {
  const i18nPage = t(`staticPage.pages.${slug}`, { returnObjects: true });
  if (!i18nPage || typeof i18nPage !== 'object' || Array.isArray(i18nPage) || !i18nPage.content) {
    return null;
  }
  return i18nPage;
}

function applyStaticPageTranslation(pageData, lang, t) {
  if (!pageData || lang === 'tr' || pageData.current_language === lang) {
    return pageData;
  }
  const i18nPage = getI18nStaticPageContent(t, pageData.slug);
  if (!i18nPage) {
    return pageData;
  }
  return {
    ...pageData,
    title: i18nPage.title || pageData.title,
    content: i18nPage.content || pageData.content,
    meta_description: i18nPage.metaDescription || i18nPage.meta_description || pageData.meta_description,
    current_language: lang,
  };
}

function getStaticPageSlug(pathname, paramsSlug) {
  if (paramsSlug) return resolveStaticPageSlug(paramsSlug);
  const segs = pathname.split('/').filter(Boolean);
  if (segs.length >= 2 && LANG_CODES.has(segs[0])) {
    if (segs[1] === 'page' && segs[2]) return resolveStaticPageSlug(segs[2]);
    return resolveStaticPageSlug(segs[1]);
  }
  if (segs[0] === 'page' && segs[1]) return resolveStaticPageSlug(segs[1]);
  return resolveStaticPageSlug(segs[0] || '');
}

const StaticPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageSlug = getStaticPageSlug(location.pathname, slug);
  const currentLang = (i18n.language || 'tr').substring(0, 2);
  const homePath = buildPathForLocale(currentLang, []);
  const site = getPublicSiteUrl();
  const pageUrl = site ? `${site}${location.pathname}` : '';

  useEffect(() => {
    fetchPage();
  }, [pageSlug, currentLang]);

  const fetchPage = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/pages/${pageSlug}?lang=${currentLang}`);
      setPage(applyStaticPageTranslation(res.data, currentLang, t));
    } catch (err) {
      console.error('Page fetch error:', err);
      setError(t('staticPage.notFound'));
    } finally {
      setLoading(false);
    }
  };

  const metaDescription = useMemo(() => {
    if (!page) return '';
    if (page.meta_description) return page.meta_description;
    return stripHtmlForMeta(page.content, 160);
  }, [page]);

  const breadcrumbUi = useMemo(() => {
    if (!page) return [];
    return [
      { label: t('common.home'), to: homePath },
      { label: page.title, to: null },
    ];
  }, [page, t, homePath]);

  const breadcrumbLd = useMemo(() => {
    if (!site || !page || !pageUrl) return '';
    const homeUrl = site + (homePath === '/' ? '' : homePath);
    return jsonLdScript(
      breadcrumbListJsonLd([
        { name: t('common.home'), item: homeUrl },
        { name: page.title, item: pageUrl },
      ]),
    );
  }, [site, page, pageUrl, homePath, t]);

  const webPageLd = useMemo(() => {
    if (!pageUrl || !page) return '';
    return jsonLdScript(
      webPageJsonLd({
        url: pageUrl,
        name: page.title,
        description: metaDescription,
        site,
      }),
    );
  }, [pageUrl, page, metaDescription, site]);

  const formattedDate = useMemo(() => {
    if (!page?.updated_at) return '';
    const locale = DATE_LOCALE_MAP[currentLang] || 'en-US';
    return new Date(page.updated_at).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [page, currentLang]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-6">{error || t('staticPage.notFound')}</p>
          <Link to={homePath}>
            <Button>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('staticPage.backToHome')}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="static-page" data-seo-ready="true">
      <Helmet htmlAttributes={{ lang: currentLang }}>
        <title>{`${page.title} | Turkish E-Market`}</title>
        <meta name="description" content={metaDescription} />
        {pageUrl ? <link rel="canonical" href={pageUrl} /> : null}
        {breadcrumbLd ? <script type="application/ld+json">{breadcrumbLd}</script> : null}
        {webPageLd ? <script type="application/ld+json">{webPageLd}</script> : null}
      </Helmet>
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 md:px-8 py-4">
          <PageBreadcrumb items={breadcrumbUi} />
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-8 py-12">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm p-8 md:p-12">
          <div
            className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900 prose-blockquote:border-l-primary prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-lg"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />

          <div className="mt-12 pt-6 border-t text-sm text-gray-500">
            {t('staticPage.lastUpdated')} {formattedDate}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaticPage;
